// web/src/data/rank.ts
import {
  Timestamp,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
  getDoc,
  type Query,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

/* =========================
   Config (unchanged)
========================= */
export const RANK_CAPS = {
  cards: 500,
  msgs: 30,
  followers: 100,
  trades: 60,
  ratings: 50,
  activity: 60,
};

export const RANK_WEIGHTS = {
  cards: 0.2,
  msgs: 1,
  followers: 2,
  trades: 5,
  ratings: 4,
  activity: 1,
};

export type RankTitle =
  | "Grand Arcanist"
  | "Elder Archmage"
  | "Supreme Sorcerer"
  | "Mystic Sage"
  | "Master of the Arcane"
  | "Archmage"
  | "Wizard"
  | "Sorcerer"
  | "Mage"
  | "Acolyte"
  | "Apprentice"
  | "Initiate";

export type ComputedMetrics = {
  cards_in_collection: number;
  messages_received_30d_unique: number;
  followers_count: number;
  trades_completed: number;
  positive_ratings: number;
  site_activity_60d: number;
};

export function computeTotalScore(m: ComputedMetrics): number {
  const cards =
    Math.min(m.cards_in_collection || 0, RANK_CAPS.cards) * RANK_WEIGHTS.cards;
  const msgs =
    Math.min(m.messages_received_30d_unique || 0, RANK_CAPS.msgs) *
    RANK_WEIGHTS.msgs;
  const followers =
    Math.min(m.followers_count || 0, RANK_CAPS.followers) *
    RANK_WEIGHTS.followers;
  const trades =
    Math.min(m.trades_completed || 0, RANK_CAPS.trades) *
    RANK_WEIGHTS.trades;
  const ratings =
    Math.min(m.positive_ratings || 0, RANK_CAPS.ratings) *
    RANK_WEIGHTS.ratings;
  const activity =
    Math.min(m.site_activity_60d || 0, RANK_CAPS.activity) *
    RANK_WEIGHTS.activity;

  return Math.round((cards + msgs + followers + trades + ratings + activity) * 10) / 10;
}

export function deriveRank(score: number, username?: string | null): RankTitle {
  // Special case, as in your reference util
  if (username && username.toLowerCase() === "evictme") return "Grand Arcanist";
  if (score >= 300) return "Elder Archmage";
  if (score >= 220) return "Supreme Sorcerer";
  if (score >= 160) return "Mystic Sage";
  if (score >= 120) return "Master of the Arcane";
  if (score >= 80) return "Archmage";
  if (score >= 50) return "Wizard";
  if (score >= 30) return "Sorcerer";
  if (score >= 20) return "Mage";
  if (score >= 10) return "Acolyte";
  if (score >= 5) return "Apprentice";
  return "Initiate";
}

/* =========================
   Helpers
========================= */
async function safeCount(_qy: Query<DocumentData>): Promise<number> {
  // Aggregator removed; return placeholder.
  // If needed later, replace with: (await getDocs(_qy)).size
  return 0;
}

async function safeList<T extends Record<string, unknown> = Record<string, unknown>>(
  qy: Query<DocumentData>,
  max = 500,
): Promise<Array<T & { id: string }>> {
  try {
    const snap = await getDocs(qy);
    return snap.docs.slice(0, max).map((d) => ({ id: d.id, ...(d.data() as T) }));
  } catch {
    return [];
  }
}

/* =========================
   Firestore-driven metrics
   - Messages/Activity derived from top-level `conversations`
     to match your rules + indexes (participants + lastMessageAt/updatedAt)
========================= */
export async function computeMetrics(uid: string): Promise<ComputedMetrics> {
  const now = new Date();
  const thirtyDaysAgo = Timestamp.fromDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
  const sixtyDaysAgo  = Timestamp.fromDate(new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000));

  // Subcollections under /users/{uid}
  const cardsCol     = collection(db, "users", uid, "cards");
  const followersCol = collection(db, "users", uid, "followers");
  const tradesCol    = collection(db, "users", uid, "trades");
  const ratingsCol   = collection(db, "users", uid, "ratings");

  // Conversations scan removed. Use messages_v2 targeted queries instead.

  type MsgDoc = { senderId?: string; recipientId?: string; createdAt?: Timestamp };

  const [
    cardsCount,
    followersCount,
    tradesCount,
    positiveRatingsCount,
    msgsToMe30d,
    msgsSent60d,
    msgsRecv60d,
  ] = await Promise.all([
    safeCount(query(cardsCol)),
    safeCount(query(followersCol)),
    safeCount(query(tradesCol, where("status", "==", "completed"))),
    safeCount(query(ratingsCol, where("score", ">=", 4))),
    // Unique senders to me in last 30d
    safeList<MsgDoc>(
      query(
        collection(db, "messages_v2"),
        where("recipientId", "==", uid),
        where("createdAt", ">=", thirtyDaysAgo),
        limit(1000)
      ),
      1000
    ),
    // Sent in last 60d
    safeList<MsgDoc>(
      query(
        collection(db, "messages_v2"),
        where("senderId", "==", uid),
        where("createdAt", ">=", sixtyDaysAgo),
        limit(1000)
      ),
      1000
    ),
    // Received in last 60d
    safeList<MsgDoc>(
      query(
        collection(db, "messages_v2"),
        where("recipientId", "==", uid),
        where("createdAt", ">=", sixtyDaysAgo),
        limit(1000)
      ),
      1000
    ),
  ]);

  // Messages received in last 30d (unique senders)
  const messages_received_30d_unique = new Set(
    msgsToMe30d
      .map((m) => (typeof m.senderId === "string" ? m.senderId : null))
      .filter(Boolean) as string[]
  ).size;

  // Site activity in last 60d:
  // recent card updates + any message activity (sent or received)
  const [recentCards] = await Promise.all([
    safeList<{ createdAt?: Timestamp; updatedAt?: Timestamp }>(
      query(cardsCol, orderBy("updatedAt", "desc"), limit(1000)),
      1000
    ),
  ]);

  const activityDays = new Set<string>();
  recentCards.forEach((r) => {
    const t =
      (r.updatedAt instanceof Timestamp && r.updatedAt) ||
      (r.createdAt instanceof Timestamp && r.createdAt) ||
      null;
    if (t && t.toMillis() >= sixtyDaysAgo.toMillis()) {
      activityDays.add(t.toDate().toISOString().slice(0, 10));
    }
  });
  [...msgsSent60d, ...msgsRecv60d].forEach((m) => {
    if (m.createdAt instanceof Timestamp) {
      activityDays.add(m.createdAt.toDate().toISOString().slice(0, 10));
    }
  });

  return {
    cards_in_collection: cardsCount,
    messages_received_30d_unique,
    followers_count: followersCount,
    trades_completed: tradesCount,
    positive_ratings: positiveRatingsCount,
    site_activity_60d: activityDays.size,
  };
}

/* =========================
   Public API
========================= */
export async function updateUserRank(uid: string): Promise<{
  score: number;
  rank: RankTitle;
  metrics: ComputedMetrics;
} | null> {
  try {
    // Read username to support the "evictme" override
    const uref = doc(db, "users", uid);
    const usnap = await getDoc(uref);
    const rawUser = usnap.exists() ? (usnap.data() as Record<string, unknown>) : null;
    const username: string | null =
      rawUser && typeof rawUser["username"] === "string"
        ? (rawUser["username"] as string)
        : null;

    const metrics = await computeMetrics(uid);
    const score = computeTotalScore(metrics);
    const rank = deriveRank(score, username);

    await updateDoc(uref, {
      total_score: score,
      rank_title: rank,
      rank_updated_at: serverTimestamp(),
      metrics,
    });

    return { score, rank, metrics };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("updateUserRank failed:", e);
    return null;
  }
}

/* =========================
   Debounced trigger
========================= */
const timers = new Map<string, ReturnType<typeof setTimeout>>();
export function triggerRankUpdate(uid: string, reason: string) {
  if (!uid) return;
  const key = `${uid}:${reason || "rank"}`;
  const existing = timers.get(key);
  if (existing) clearTimeout(existing);
  timers.set(
    key,
    setTimeout(() => {
      updateUserRank(uid);
      timers.delete(key);
    }, 1500),
  );
}

/* =========================
   Heads-up on indexes
========================= */
// You’ll likely need composite indexes on messages_v2:
//   recipientId + createdAt (ASC/DESC)
//   senderId + createdAt (ASC/DESC)

// If prompted by Firestore, click the console link or add them to firestore.indexes.json and deploy.

